import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NavLink} from 'react-router-dom';
import cn from 'classnames';
import {map, filter, padStart} from 'lodash';
import api from 'api';
import {Statuses, useStatus} from '../../../utils/hooks';
import {getCheckInDate, getMembers, getNumberOfGuests} from 'utils/reservation';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {resetPersonalDataFormStorage} from '../../AddPersonalDataForm';
import {getTextDescriptionNumberOfGuests} from '../../../utils/common';
import {PATHS} from '../../../Routes';
import {Guest, GUEST_STATUSES} from '../../../utils/types';
import {useReservation} from 'context/reservation';
import deleteGuestIcon from 'assets/delete-guest-icon.png';
import StyledIcon from '../StyledIcon';
import Button from 'components/acommon/Button';
import Modal from 'components/Modal';
import styles from './BookingStatus.module.sass';
import {
  GuestList,
  GuestLine,
  GuestNumber,
  GuestName,
  DeleteButton,
  // EditButton,
  NewGuestButton,
  DeleteGuestModal,
} from './styled';

type BookingStatusProps = {
  isOnFinishPage?: boolean;
  className?: string;
};
function BookingStatus({className, isOnFinishPage}: BookingStatusProps) {
  const {t, i18n} = useTranslation();
  const {isLoading, setStatus} = useStatus();
  const successDeletedTimeoutRef = React.useRef<any>(null);
  const {data: reservation, refreshReservation} = useReservation();
  const {
    hasBiomatch,
    hasScanDocument,
    isLeaderGuest,
    formattedCheckInDate,
    formattedCheckOutDate,
  } = useComputedReservationDetails();
  const [guests, setGuests] = useState<Guest[]>();
  const [guestIdToDelete, setGuestIdToDelete] = useState<string | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const checkInDate = getCheckInDate(reservation);
  const canDeleteGuests = Boolean(checkInDate && checkInDate > new Date());

  const numberOfGuests = getNumberOfGuests(reservation);

  const textDescriptionNumberOfGuests = getTextDescriptionNumberOfGuests(numberOfGuests, {
    language: i18n.language,
    t,
  });

  useEffect(() => {
    setGuests(getMembers(reservation));
  }, [reservation]);

  const handleDeleteGuest = async () => {
    setGuests(filter(guests, ({id}) => id !== guestIdToDelete));
    if (guestIdToDelete) {
      setStatus(Statuses.loading);
      await api.guests.deleteGuest(guestIdToDelete);
      await refreshReservation();
      setStatus(Statuses.idle);
    }
    setIsDeleted(true);
    successDeletedTimeoutRef.current = setTimeout(() => {
      setGuestIdToDelete(null);
      setIsDeleted(false);
    }, 1000);
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(successDeletedTimeoutRef.current);
    };
  }, []);

  // const handleEditGuest = async (guestId: string) => {
  //   history.push('/form/editguest', {...location.state, guestId});
  //   localStorage.removeItem('photosState');
  // };

  const getNewGuestPath = () => {
    if (isLeaderGuest) {
      return PATHS.confirmBooking;
    }
    if (hasBiomatch || hasScanDocument) {
      return PATHS.verificationStart;
    }
    return PATHS.formPersonalData;
  };

  const getDeleteButtonLabel = () => {
    if (isLoading) return `${t('loading')}...`;
    if (isDeleted) return t('deleted');
    return t('yes_delete');
  };

  const handleCloseDeleteModal = () => setGuestIdToDelete(null);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.overview}>
        <div className={styles.overviewItem}>
          <StyledIcon className={styles.overviewIcon} name="dateIcon" size="48px" />
          <div className={styles.overviewDetails}>
            <div className={styles.overviewName}>{t('dates')}</div>
            <div className={styles.overviewText}>{formattedCheckInDate}</div>
            <div className={styles.overviewText}>{formattedCheckOutDate}</div>
          </div>
        </div>
        <div className={styles.overviewItem}>
          <StyledIcon className={styles.overviewIcon} name="guestsIcon" size="48px" />
          <div className={styles.overviewDetails}>
            <div className={styles.overviewName}>{t('guests')}</div>
            <div className={styles.overviewText}>{textDescriptionNumberOfGuests}</div>
          </div>
        </div>
      </div>

      <div
        className={cn(styles.status, {
          [styles.statusDealsFinishPage]: isOnFinishPage,
        })}
      >
        {t('checkin_status')}{' '}
        <span className={styles.statusDetails}>
          ({guests?.length}/{numberOfGuests} {t('registered')})
        </span>
      </div>
      <GuestList className={styles.list}>
        {guests?.length
          ? map(guests, (guest, number) => {
              const isComplate = guest?.status === GUEST_STATUSES.complete;
              return (
                <GuestLine key={guest.id}>
                  <GuestNumber isComplete={isComplate}>
                    {padStart(String(number + 1), 2, '0')}
                  </GuestNumber>
                  <GuestName title={guest.status}>
                    {`${guest.names.name} ${guest.names.surname}`}
                  </GuestName>
                  {/*-----------Future Feature------------*/}
                  {/*<div style={{marginRight: '10px'}}>*/}
                  {/*  {isComplate ? (*/}
                  {/*    <>*/}
                  {/*      <EditButton onClick={() => handleEditGuest(guest.id)}>*/}
                  {/*        <StyledIcon name="editguest" color="#6B6B95" />*/}
                  {/*      </EditButton>*/}
                  {/*    </>*/}
                  {/*  ) : (*/}
                  {/*    <Button size="small" to={{pathname: '/?signup=true'}}>*/}
                  {/*      Check-in*/}
                  {/*    </Button>*/}
                  {/*  )}*/}
                  {/*</div>*/}
                  {/*-----------Future Feature------------*/}
                  <div>
                    {canDeleteGuests && (
                      <DeleteButton onClick={() => setGuestIdToDelete(guest.id)}>
                        <StyledIcon
                          name="removeguest"
                          title={t<string>('remove_guest')}
                          color="#6B6B95"
                        />
                      </DeleteButton>
                    )}
                  </div>
                </GuestLine>
              );
            })
          : t('no_guests')}
      </GuestList>
      <NavLink to={getNewGuestPath()} onClick={resetPersonalDataFormStorage}>
        <NewGuestButton>
          <StyledIcon name="addGuest" color="#2148FF" />
          <span>{t('new_guest')}</span>
        </NewGuestButton>
      </NavLink>

      <Modal
        open={Boolean(guestIdToDelete)}
        onClose={handleCloseDeleteModal}
        closeOnDocumentClick
        closeOnEscape
        iconSrc={deleteGuestIcon}
        iconProps={{width: 110, height: 110}}
        lockScroll={false}
      >
        <DeleteGuestModal>
          <div className="delete-guest-modal_title">{t('delete_guest_question')}</div>
          <div className="delete-guest-modal_text">{t('delete_guest_description')}</div>
          <div className="delete-guest-modal_buttons">
            <Button
              disabled={isLoading || isDeleted}
              label={getDeleteButtonLabel()}
              onClick={handleDeleteGuest}
              danger
            />
            <Button
              disabled={isLoading || isDeleted}
              label={t('cancel')}
              onClick={handleCloseDeleteModal}
              link
            />
          </div>
        </DeleteGuestModal>
      </Modal>
    </div>
  );
}

export {BookingStatus};
